/**
 * El rótulo (ícono + nombre en castellano) de cada tool de Gustavo, para el chip que
 * queda en la burbuja. Antes había 50 y el resto se veía con el nombre crudo
 * (`get_property_activity_report`, `abrir_capacidades`); pedido del user, 30/10/2026.
 *
 * Cubre el catálogo del backend (`src/modules/ai/tools`). Una tool que no esté acá cae
 * en `etiquetaGenerica`, que arma algo legible del nombre: nunca se ve snake_case.
 */
const ETIQUETAS: Record<string, string> = {
  // Propiedades
  search_properties:            '🏠 Propiedades',
  get_property:                 '🏠 Ficha propiedad',
  my_properties:                '🏠 Mis propiedades',
  my_properties_performance:    '📈 Rendimiento de mis propiedades',
  get_property_activity_report: '📈 Actividad de la propiedad',
  get_property_inventory:       '📋 Inventario de la propiedad',
  create_property:              '🏠 Crear propiedad',
  update_property:              '✏️ Editar propiedad',
  update_property_status:       '🔄 Cambiar estado propiedad',
  reserve_property:             '🔖 Reservar propiedad',
  update_reservation_note:      '🔖 Nota de la reserva',
  clone_property:               '📑 Duplicar propiedad',
  discard_property_draft:       '🗑️ Descartar borrador',
  diagnose_property:            '🩺 Diagnóstico de la propiedad',
  listing_quality:              '🩺 Calidad del aviso',
  generate_property_description: '✍️ Descripción para portal',
  enrich_property_geo:          '📍 Ubicación de la propiedad',
  find_properties_near:         '📍 Propiedades cerca',
  check_property_exclusivity:   '🔏 Exclusividad',
  set_property_temperature:     '🌡️ Temperatura de la propiedad',
  set_property_fuera_del_agente: '🚫 Fuera del agente',
  toggle_property_featured:     '⭐ Destacar propiedad',
  apply_featured_slot:          '⭐ Usar cupo destacado',
  get_my_marketing_slots:       '⭐ Mis cupos destacados',
  create_property_note:         '📝 Nota de la propiedad',
  get_case_by_property:         '📁 Caso de la propiedad',
  search_property_pool:         '🤝 Bolsa de propiedades',
  contact_pool_agent:           '🤝 Contactar agente de la bolsa',
  get_blind_link:               '🔗 Link ciego',
  generate_owner_portal_link:   '🔑 Portal del propietario',
  market_analysis_by_zone:      '📊 Análisis de zona',
  consultar_catastro:           '🏛️ Catastro',
  // Fotos y Studio
  add_property_images:          '📷 Subir fotos',
  delete_property_images:       '🗑️ Borrar fotos',
  list_property_media:          '🖼️ Fotos de la propiedad',
  reorder_property_media:       '🖼️ Ordenar fotos',
  set_property_main_media:      '🖼️ Foto principal',
  send_property_photos:         '📷 Enviar fotos',
  analyze_property_images:      '🔍 Analizar fotos',
  rare_photo:                   '🔍 Foto rara',
  read_attachment:              '📎 Leer adjunto',
  studio_edit_property_photo:   '🎨 Studio: editar foto',
  studio_enhance_loose_photo:   '🎨 Studio: mejorar foto',
  studio_decide_result:         '🎨 Studio: decidir resultado',
  crear_pieza_redes:            '📣 Pieza para redes',
  // Portales
  publish_property_external:    '📤 Publicar en portales',
  republish_property_external:  '🔁 Republicar en portales',
  republish_properties_external: '🔁 Republicar propiedades',
  sync_property_meli:           '🟡 Sincronizar con MercadoLibre',
  unpublish_property_meli:      '🟡 Despublicar de MercadoLibre',
  feature_property_meli:        '🟡 Destacar en MercadoLibre',
  check_meli_listing_status:    '🟡 Estado en MercadoLibre',
  find_property_by_meli:        '🟡 Buscar por MercadoLibre',
  responder_pregunta_meli:      '🟡 Responder pregunta de MercadoLibre',
  meli_gustavo_contact:         '🟡 Contacto de MercadoLibre',
  find_property_by_infocasas:   '🔵 Buscar por InfoCasas',
  search_external_listings:     '🌐 Avisos externos',
  capture_external_listing:     '🌐 Captar aviso externo',
  my_customers_publishing:      '📤 Mis clientes publicando',
  // Clientes
  search_customers:             '👥 Clientes',
  get_customer:                 '👤 Ficha cliente',
  create_customer:              '➕ Crear cliente',
  update_customer:              '✏️ Actualizar cliente',
  close_customer:               '🔒 Cerrar cliente',
  summarize_customer:           '🧠 Resumen del cliente',
  suggest_next_action_for_customer: '💡 Próximo paso con el cliente',
  client_followup:              '📞 Seguimiento del cliente',
  find_duplicate_customers:     '👥 Clientes duplicados',
  link_customer:                '🔗 Vincular cliente',
  unlink_customer:              '🔗 Desvincular cliente',
  add_customer_relation:        '👪 Relación del cliente',
  get_customer_relations:       '👪 Relaciones del cliente',
  add_customer_important_date:  '🎂 Fecha importante',
  list_customer_important_dates: '🎂 Fechas importantes',
  suggest_birthday_followups:   '🎂 Cumpleaños para saludar',
  add_property_to_customer_interests: '💛 Agregar interés',
  remove_property_from_customer_interests: '💔 Quitar interés',
  add_customer_search_interest: '🔎 Registrar búsqueda del cliente',
  match_customer_to_properties: '🎯 Propiedades para cliente',
  match_property_to_customers:  '👥 Clientes para propiedad',
  inactive_customers_alert:     '🧊 Clientes fríos',
  hot_leads_today:              '🔥 Clientes calientes',
  detect_high_intent_leads:     '🔥 Clientes con intención',
  transfer_customer_to_agent:   '🔁 Transferir cliente',
  manage_customer_portal_access: '🔑 Acceso del cliente al portal',
  draft_customer_email:         '✉️ Borrador de email',
  draft_whatsapp_message:       '💬 Borrador de WhatsApp',
  build_whatsapp_link:          '💬 Link de WhatsApp',
  escribir_al_cliente:          '💬 Escribir al cliente',
  // Oportunidades y pipeline
  create_opportunity:           '🎯 Crear oportunidad',
  update_opportunity:           '✏️ Actualizar oportunidad',
  advance_opportunity_stage:    '➡️ Mover oportunidad',
  delete_opportunity:           '🗑️ Borrar oportunidad',
  my_pipeline:                  '🧭 Mi pipeline',
  detect_stale_opportunities:   '🕸️ Oportunidades frenadas',
  detect_at_risk_deals:         '⚠️ Negocios en riesgo',
  detect_properties_no_activity: '💤 Propiedades sin movimiento',
  // Notas
  create_note:                  '📝 Crear nota',
  update_note:                  '📝 Editar nota',
  delete_note:                  '🗑️ Borrar nota',
  // Agenda
  create_event:                 '📅 Agendar evento',
  update_event:                 '📆 Mover evento',
  complete_event:               '✅ Evento completado',
  cancel_event:                 '🚫 Cancelar evento',
  create_calendar_event:        '📅 Agendar evento',
  update_calendar_event:        '📆 Mover evento',
  delete_calendar_event:        '🗑️ Borrar evento',
  get_calendar_event:           '📅 Ver evento',
  list_calendar_events:         '📅 Agenda',
  get_upcoming_events:          '📅 Próximos eventos',
  schedule_showing_with_customer: '🏠📅 Agendar visita + seguimiento',
  pre_visit_brief:              '📋 Brief de la visita',
  post_visit_suggestions:       '📋 Después de la visita',
  create_reminder:              '⏰ Crear recordatorio',
  update_reminder:              '⏰ Editar recordatorio',
  complete_reminder:            '✅ Completar recordatorio',
  delete_reminder:              '🗑️ Borrar recordatorio',
  // Mi día y métricas
  get_home_today:               '🌅 Mi día',
  my_today_briefing:            '🌅 Mi día',
  daily_proactive_briefing:     '🌅 Briefing del día',
  my_performance_metrics:       '📊 Mis métricas',
  weekly_performance_summary:   '📊 Resumen de la semana',
  get_my_agent_analytics:       '📊 Mis analíticas',
  get_funnel_analytics:         '📊 Embudo',
  get_pipeline_analytics:       '📊 Analítica del pipeline',
  get_overview_analytics:       '📊 Panorama',
  get_agents_ranking:           '🏆 Ranking de agentes',
  get_offices_ranking:          '🏆 Ranking de oficinas',
  get_period_leaderboard:       '🏆 Tabla del período',
  get_my_competition_progress:  '🏁 Mi competencia',
  get_my_competition_achievements: '🏅 Mis logros de competencia',
  list_my_achievements:         '🏅 Mis logros',
  get_my_milestones:            '🏅 Mis hitos',
  get_my_millionaire_progress:  '💎 Camino al millón',
  get_my_ratings_summary:       '⭐ Mis calificaciones',
  set_agent_temperature:        '🌡️ Temperatura del agente',
  enviar_parte_diario:          '📨 Enviar parte diario',
  ver_parte_diario:             '📨 Ver parte diario',
  registrar_presentacion_comercial: '🤝 Registrar presentación',
  // Captación y tasación
  create_captation_lead:        '🎣 Nueva captación',
  list_captaciones:             '🎣 Mis captaciones',
  get_captacion:                '🎣 Ficha captación',
  update_captacion:             '✏️ Editar captación',
  update_captacion_status:      '🔄 Estado de la captación',
  set_captacion_appraisal:      '📐 Valuación de la captación',
  discard_captacion:            '🗑️ Descartar captación',
  reactivate_captacion:         '♻️ Reactivar captación',
  delete_captacion:             '🗑️ Borrar captación',
  renew_captations:             '♻️ Renovar captaciones',
  search_capture_board:         '🎣 Tablero de captación',
  capture_profile:              '🎣 Perfil de captación',
  capture_proposals:            '🎣 Propuestas de captación',
  start_valuation:              '📐 Iniciar tasación',
  create_tasacion:              '📐 Crear tasación',
  get_tasacion:                 '📐 Ver tasación',
  list_tasaciones:              '📐 Mis tasaciones',
  update_tasacion:              '✏️ Editar tasación',
  update_tasacion_comparables:  '📐 Comparables',
  compute_tasacion:             '📐 Calcular tasación',
  derive_tasacion:              '📐 Derivar tasación',
  suggest_tasacion_price:       '📐 Precio sugerido',
  get_tasacion_price_line:      '📐 Línea de precio',
  complete_tasacion:            '✅ Cerrar tasación',
  delete_tasacion:              '🗑️ Borrar tasación',
  generate_tasacion_pdf:        '📄 PDF de la tasación',
  // Alquileres, casos y escribanía
  create_rental_inventory:      '📋 Inventario de alquiler',
  generate_rental_inventory_pdf: '📄 PDF del inventario',
  start_rental_checkout:        '🧾 Seña / checkout de alquiler',
  list_my_rental_cases:         '📁 Mis casos de alquiler',
  list_my_sale_cases:           '📁 Mis casos de venta',
  list_case_documents:          '📁 Documentos del caso',
  add_case_note:                '📝 Nota del caso',
  // Hipotecas
  get_mortgage_prequalification: '🏦 Precalificación',
  list_mortgage_prequalifications: '🏦 Precalificaciones',
  update_mortgage_prequalification: '✏️ Editar precalificación',
  update_mortgage_status:       '🔄 Estado de la hipoteca',
  send_mortgage_to_bank:        '🏦 Enviar al banco',
  add_mortgage_note:            '📝 Nota de la hipoteca',
  // Comisiones, wallet y créditos
  get_commission:               '💵 Comisión',
  list_commissions:             '💵 Mis comisiones',
  preview_commission:           '💵 Simular comisión',
  pay_commission_split:         '💵 Pagar reparto',
  get_my_finance_dashboard:     '💵 Mis finanzas',
  get_my_wallet_balance:        '👛 Mi saldo',
  list_wallet_movements:        '👛 Movimientos',
  create_withdrawal_request:    '👛 Pedir retiro',
  list_my_withdrawal_requests:  '👛 Mis retiros',
  cancel_withdrawal_request:    '👛 Cancelar retiro',
  peer_transfer_to_agent:       '👛 Transferir a un colega',
  get_my_credits:               '🪙 Mis créditos',
  list_my_credit_transactions:  '🪙 Movimientos de créditos',
  convert_currency:             '💱 Convertir moneda',
  // Marketplace e inversiones
  list_marketplace_products:    '🛒 Marketplace',
  purchase_product:             '🛒 Comprar',
  pay_order_with_credits:       '🛒 Pagar con créditos',
  pay_order_with_wallet:        '🛒 Pagar con saldo',
  list_my_orders:               '🛒 Mis pedidos',
  cancel_marketplace_order:     '🛒 Cancelar pedido',
  list_investment_catalog:      '📈 Inversiones',
  allocate_investment_slot:     '📈 Invertir',
  extend_investment_allocation: '📈 Extender inversión',
  cancel_allocation:            '📈 Cancelar inversión',
  list_my_allocations:          '📈 Mis inversiones',
  get_my_investment_score:      '📈 Mi score inversor',
  // Subastas
  list_auctions:                '🏷️ Subastas',
  list_my_auctions:             '🏷️ Mis subastas',
  get_my_auctions_overview:     '🏷️ Resumen de subastas',
  get_auction:                  '🏷️ Ver subasta',
  create_auction:               '🏷️ Crear subasta',
  update_auction:               '✏️ Editar subasta',
  approve_auction:              '✅ Aprobar subasta',
  cancel_auction:               '🚫 Cancelar subasta',
  list_auction_bids:            '🏷️ Ofertas de la subasta',
  list_auction_participants:    '🏷️ Participantes',
  approve_auction_participant:  '✅ Aprobar participante',
  reject_auction_participant:   '🚫 Rechazar participante',
  summarize_auction_activity:   '🏷️ Actividad de la subasta',
  // Chat, agentes y oficinas
  search_agents:                '🧑‍💼 Buscar agente',
  get_my_colleagues:            '🧑‍💼 Mis colegas',
  get_online_agents:            '🟢 Agentes en línea',
  get_my_circles:               '🧑‍🤝‍🧑 Mis círculos',
  send_agent_message:           '💬 Enviar mensaje',
  send_group_message:           '📢 Mensaje a grupo',
  start_direct_chat:            '💬 Abrir chat',
  create_group_chat:            '👥 Crear grupo',
  list_chat_groups:             '👥 Listar grupos',
  list_chat_channels:           '💬 Canales del chat',
  list_chat_messages:           '💬 Mensajes del chat',
  mark_chat_read:               '✅ Chat leído',
  get_office:                   '🏢 Mi oficina',
  list_offices:                 '🏢 Oficinas',
  get_user_profile:             '👤 Perfil del agente',
  get_me_profile:               '👤 Mi perfil',
  admin_reset_agent_password:   '🔐 Resetear contraseña',
  // Phylax (Agente Virtual) y consultas
  registrar_consulta:           '📨 Registrar consulta',
  ver_consulta:                 '📨 Ver consulta',
  responder_consulta:           '📨 Responder consulta',
  cerrar_consulta:              '✅ Cerrar consulta',
  rechazar_consulta:            '🚫 Rechazar consulta',
  my_escalations:               '🚨 Mis escalaciones',
  get_estado_agente_virtual:    '🤖 Estado del Agente Virtual',
  charla_con_el_agente_virtual: '🤖 Charla del Agente Virtual',
  mi_pagina_publica:            '🌐 Mi página pública',
  // Notificaciones, novedades y capacitaciones
  list_my_notifications:        '🔔 Mis notificaciones',
  summarize_unread_notifications: '🔔 Resumen de notificaciones',
  mark_notification_read:       '✅ Notificación leída',
  mark_all_notifications_read:  '✅ Todo leído',
  mark_showcase_seen:           '✅ Novedad vista',
  list_blog_posts:              '📰 Novedades',
  get_blog_post:                '📰 Ver novedad',
  summarize_blog_post:          '📰 Resumir novedad',
  list_capacitaciones:          '🎓 Capacitaciones',
  get_capacitacion:             '🎓 Ver capacitación',
  get_capacitacion_overview:    '🎓 Mi avance',
  complete_capacitacion_lesson: '✅ Lección completada',
  submit_capacitacion_exam:     '🎓 Entregar examen',
  unlock_capacitacion:          '🔓 Desbloquear capacitación',
  // Gustavo mismo
  search_in_help:               '📖 Ayuda',
  buscar_en_la_charla:          '🔎 Nuestra charla',
  abrir_capacidades:            '🧰 Ver qué puedo hacer',
  remember_about_me:            '🧠 Recordar sobre vos',
  list_my_memories:             '🧠 Lo que recuerdo',
  list_my_user_memories:        '🧠 Lo que recuerdo',
  delete_my_user_memory:        '🧠 Olvidar un dato',
  forget_about_me:              '🧠 Olvidar todo',
  skip_gustavo_onboarding:      '⏭️ Saltar onboarding',
  report_bug:                   '🐞 Reportar problema',
  demo:                         '🧪 Demo',
  deshabilitada:                '⛔ Deshabilitada',
};

const ICONO_POR_VERBO: [RegExp, string][] = [
  [/^(search|find|list|get|my_|ver_|detect)/, '🔎'],
  [/^(create|add|crear|registrar|start)/,      '➕'],
  [/^(update|edit|set|reorder|rename)/,        '✏️'],
  [/^(delete|remove|discard|cancel|borrar)/,   '🗑️'],
  [/^(send|enviar|escribir|publish|responder)/, '📤'],
  [/^(complete|approve|mark|cerrar)/,          '✅'],
];

/** Para una tool sin rótulo: ícono por el verbo y el nombre en palabras. */
export function etiquetaGenerica(name: string): string {
  const icono = ICONO_POR_VERBO.find(([re]) => re.test(name))?.[1] ?? '⚡';
  const palabras = name.replace(/_/g, ' ').trim();
  return `${icono} ${palabras.charAt(0).toUpperCase()}${palabras.slice(1)}`;
}

export function etiquetaDeTool(name: string): string {
  return ETIQUETAS[name] ?? etiquetaGenerica(name);
}
